import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errText } from "./docFile";
import { PodSection } from "./PodSection";

const FIELDS = [
  ["ship_through", "Ship Through", "text", "e.g. Blue Dart"], ["awb", "AWB Number", "text", "Courier tracking number"],
  ["boxes", "Number of Boxes", "number", "1"], ["weight_kg", "Weight (kg)", "number", "0.0"],
  ["dispatch_date", "Dispatch Date", "date", ""], ["shipping_location", "Shipping Location", "text", "City / hub"],
  ["recipient_contact", "Recipient Contact", "text", "Phone / alternate"], ["tracking_url", "Tracking URL", "url", "https://"],
];

export function DispatchFieldsDialog({ dc, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (dc) setF(Object.fromEntries(FIELDS.map(([k]) => [k, dc[k] ?? ""]).concat([["reason", ""]]))); }, [dc]);
  if (!dc) return null;
  const locked = ["DELIVERED", "CANCELLED"].includes(dc.status);
  const awbChange = dc.awb && f.awb && f.awb !== dc.awb;
  const save = async () => {
    setBusy(true);
    const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => [k, ["boxes"].includes(k) ? parseInt(v, 10) : k === "weight_kg" ? parseFloat(v) : v]));
    try { const { data } = await api.put(`/document-centre/dcs/${dc.id}/dispatch`, body); toast.success(data.changed.length ? `Saved: ${data.changed.join(", ")}` : "No changes"); onSaved(); onClose(); }
    catch (e) { toast.error(await errText(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dispatch-fields-dialog">
        <DialogHeader><DialogTitle className="text-[14px] font-bold">Dispatch & Tracking · {dc.dc_ref}</DialogTitle></DialogHeader>
        <p className="-mt-2 text-[11px] text-slate-500">{dc.recipient_name} · {dc.location || "—"}. Ship Through, AWB No., Box, Weight and Dispatch Date print in the Courier Details block on the DC.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label, type, ph]) => (
            <label key={k} className="text-[11px] font-semibold text-slate-600">{label}
              <input type={type} value={f[k] ?? ""} placeholder={ph} disabled={locked} min={type === "number" ? 0 : undefined} step={k === "weight_kg" ? "0.1" : undefined}
                onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[12.5px] font-normal text-slate-900 focus:border-sky-500 focus:outline-none disabled:bg-slate-50" data-testid={`dispatch-field-${k}`} />
            </label>))}
          {awbChange && <label className="text-[11px] font-semibold text-amber-700 sm:col-span-2">Reason for AWB correction (required)
            <input value={f.reason} onChange={(e) => setF((x) => ({ ...x, reason: e.target.value }))} className="mt-1 w-full rounded-md border border-amber-300 px-2.5 py-1.5 text-[12.5px] font-normal" data-testid="dispatch-field-reason" /></label>}
        </div>
        <PodSection dc={dc} onSaved={onSaved} />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-full border border-slate-300 px-4 py-1.5 text-[12px] font-semibold" data-testid="dispatch-cancel">Close</button>
          {!locked && <button onClick={save} disabled={busy} className="rounded-full bg-slate-900 px-4 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-slate-700 disabled:opacity-50" data-testid="dispatch-save">{busy ? "Saving…" : "Save dispatch details"}</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
